/**
 * Stat type filtering for Team Shape visualization
 * Categorizes stat types as attacking, defensive, or neutral
 */

export type ShapeType = 'attacking' | 'defensive' | 'both';

/**
 * Stat types that should be excluded for defensive shape
 * (attacking actions: crosses, shots, goals, corners)
 */
const ATTACKING_STAT_TYPES = new Set([
  // Shots
  'shot_inside_box_goal',
  'shot_inside_box_on_target',
  'shot_inside_box_off_target',
  'shot_inside_box_blocked',
  'shot_inside_box_side_post',
  'shot_inside_box_crossbar',
  'shot_outside_box_goal',
  'shot_outside_box_on_target',
  'shot_outside_box_off_target',
  'shot_outside_box_blocked',
  'shot_outside_box_side_post',
  'shot_outside_box_crossbar',
  'penalty_scored',
  'penalty_missed',
  'penalty_shootout_scored',
  'penalty_shootout_missed',
  'own_goal',
  // Crosses
  'cross_success',
  'cross_failure',
  'cross_backward_success',
  'cross_backward_failure',
  // Corners
  'corner',
]);

/**
 * Stat types that should be excluded for attacking shape
 * (defensive actions: tackles, clearances, interceptions, backward passes)
 */
const DEFENSIVE_STAT_TYPES = new Set([
  // Defensive actions
  'tackle_success',
  'tackle_failure',
  'interception',
  'clearance',
  // Backward passes
  'short_pass_backward_success',
  'short_pass_backward_failure',
  'long_pass_backward_success',
  'long_pass_backward_failure',
  'cross_backward_success',
  'cross_backward_failure',
]);

/**
 * Check if a stat type should be included based on shape type selection
 * @param statKey - The stat_key of the stat type
 * @param shapeTypes - Array of selected shape types ('attacking', 'defensive')
 *   - Empty array or both selected = include all stat types
 *   - Only 'attacking' = exclude defensive stat types
 *   - Only 'defensive' = exclude attacking stat types
 * @returns true if the stat type should be included
 */
export function shouldIncludeStatType(statKey: string, shapeTypes: ShapeType[]): boolean {
  // If empty array or both 'attacking' and 'defensive' are selected, include all stat types
  if (shapeTypes.length === 0 || 
      (shapeTypes.includes('attacking') && shapeTypes.includes('defensive'))) {
    return true;
  }

  // If only 'defensive' is selected, exclude attacking stat types
  if (shapeTypes.includes('defensive') && !shapeTypes.includes('attacking')) {
    return !ATTACKING_STAT_TYPES.has(statKey);
  }

  // If only 'attacking' is selected, exclude defensive stat types
  if (shapeTypes.includes('attacking') && !shapeTypes.includes('defensive')) {
    return !DEFENSIVE_STAT_TYPES.has(statKey);
  }

  // Default: include all if no specific selection
  return true;
}

